import type {
  ReceiptBatchValidationContext,
  BatchReceiptDetails,
  ValidationError,
} from './types';
import { ReceiptBatchValidationError } from './types';

export function getReceiptBatchSubmissionErrors(
  context: ReceiptBatchValidationContext
): {
  errors: Array<ValidationError<ReceiptBatchValidationError>>;
  warnings: Array<ValidationError<ReceiptBatchValidationError>>;
} {
  const errors: Array<ValidationError<ReceiptBatchValidationError>> = [];
  const warnings: Array<ValidationError<ReceiptBatchValidationError>> = [];
  const {
    batchValues,
    receipts,
    isDirty,
    cashLinkFeatureEnabled,
    mandatoryCashLinkRequired,
    receiptImagesRequired,
    useIndividualDates,
    autoGenerateReceipts,
    pliantTransactionAmount,
  } = context;

  // Check for unsaved changes FIRST - if form is dirty, only return this error
  // to force the user to save first and avoid inconsistent validation states
  if (isDirty) {
    errors.push({
      type: ReceiptBatchValidationError.UNSAVED_CHANGES,
      translationKey: 'receipts.batch.validation.unsaved_changes',
      details: undefined,
    });
    // Return early - don't check other validations if there are unsaved changes
    return { errors, warnings };
  }

  // Check if batch has any receipts
  if (!receipts || receipts.length === 0) {
    errors.push({
      type: ReceiptBatchValidationError.NO_RECEIPTS_IN_BATCH,
      translationKey: 'receipts.batch.validation.no_receipts',
      details: undefined,
    });
    // If no receipts, no point checking receipt-level validations
    return { errors, warnings };
  }

  // Check for cash link - error if mandatory, warning if optional
  // Skip validation if this is an earnings batch
  if (
    !batchValues.earnings &&
    cashLinkFeatureEnabled &&
    !batchValues.cashTransactionGroupId
  ) {
    if (mandatoryCashLinkRequired) {
      errors.push({
        type: ReceiptBatchValidationError.MISSING_CASH_LINK,
        translationKey: 'receipts.batch.validation.missing_cash_link',
        details: undefined,
      });
    } else {
      warnings.push({
        type: ReceiptBatchValidationError.MISSING_CASH_LINK,
        translationKey: 'mobile.receipt.submit-cash-link-warning',
        details: undefined,
      });
    }
  }

  // Check for batch-level required fields
  if (!batchValues.title || batchValues.title.trim() === '') {
    errors.push({
      type: ReceiptBatchValidationError.MISSING_BATCH_TITLE,
      translationKey: 'receipts.batch.validation.missing_title',
      details: undefined,
    });
  }

  // Check for batch date (only if not using individual dates)
  if (!useIndividualDates && !batchValues.receiptDate) {
    errors.push({
      type: ReceiptBatchValidationError.MISSING_BATCH_DATE,
      translationKey: 'receipts.batch.validation.missing_date',
      details: undefined,
    });
  }

  // Check for receipt type
  if (!batchValues.receiptType) {
    errors.push({
      type: ReceiptBatchValidationError.MISSING_RECEIPT_TYPE,
      translationKey: 'receipts.batch.validation.missing_type',
      details: undefined,
    });
  }

  // Check for service provider if required
  if (
    batchValues.serviceProviderRequired &&
    (!batchValues.serviceProvider || batchValues.serviceProvider.trim() === '')
  ) {
    errors.push({
      type: ReceiptBatchValidationError.MISSING_SERVICE_PROVIDER,
      translationKey: 'receipts.batch.validation.missing_service_provider',
      details: undefined,
    });
  }

  // Check for batch images - error if required, warning if optional
  const hasBatchImages =
    batchValues.batchImageIds && batchValues.batchImageIds.length > 0
      ? batchValues.batchImageIds.some((id) => id && id.trim() !== '')
      : false;

  const anyReceiptHasImage = receipts.some(
    (receipt) =>
      receipt.receiptImageIds &&
      receipt.receiptImageIds.length > 0 &&
      receipt.receiptImageIds.some((id) => id && id.trim() !== '')
  );

  // If there is no batch image, and if not a single receipt has an individual image, we warn users that there is no overall image for the batch.
  // If they add individual images to any receipt, they will get the warning which other individual receipts are missing images instead.
  const showMissingBatchImageWarning =
    !hasBatchImages && !autoGenerateReceipts && !anyReceiptHasImage;

  // If receipt images are required, we don't need a warning for missing batch images, since they have to add individual images to each receipt
  // anyway. The batch image is not required.
  if (showMissingBatchImageWarning && !receiptImagesRequired) {
    // Count receipts without images for mobile compatibility
    const missingImagesCount = receipts.filter(
      (receipt) =>
        !receipt.receiptImageIds || receipt.receiptImageIds.length === 0
    ).length;

    warnings.push({
      type: ReceiptBatchValidationError.MISSING_BATCH_IMAGE,
      translationKey: 'mobile.receipt-batch.submit-warning-missing-image-title',
      details: { count: missingImagesCount },
    });
  }

  // Check receipt-level validations
  const { errors: receiptErrors, warnings: receiptWarnings } = validateReceipts(
    receipts,
    useIndividualDates,
    receiptImagesRequired,
    showMissingBatchImageWarning,
    autoGenerateReceipts
  );
  errors.push(...receiptErrors);
  warnings.push(...receiptWarnings);

  // Check for pliant transaction amount mismatch
  // Note: pliantTransactionAmount === 0 means no Pliant transaction (cash-only group)
  if (
    batchValues.cashTransactionGroupId &&
    pliantTransactionAmount !== undefined &&
    pliantTransactionAmount !== 0 &&
    receipts.length > 0
  ) {
    // Calculate total amount of all receipts in the batch
    const totalBatchAmount = receipts.reduce(
      (sum, receipt) => sum + (receipt.amount || 0),
      0
    );

    if (
      totalBatchAmount > 0 &&
      Math.abs(pliantTransactionAmount) !== Math.abs(totalBatchAmount)
    ) {
      errors.push({
        type: ReceiptBatchValidationError.PLIANT_AMOUNT_MISMATCH,
        translationKey: 'receipts.batch.validation.pliant_amount_mismatch',
        details: {
          batchAmount: totalBatchAmount,
          pliantAmount: pliantTransactionAmount,
        },
      });
    }
  }

  return { errors, warnings };
}

function validateReceipts(
  receipts: BatchReceiptDetails[],
  useIndividualDates: boolean,
  receiptImagesRequired: boolean,
  showMissingBatchImageWarning: boolean,
  autoGenerateReceipts: boolean
): {
  errors: Array<ValidationError<ReceiptBatchValidationError>>;
  warnings: Array<ValidationError<ReceiptBatchValidationError>>;
} {
  const errors: Array<ValidationError<ReceiptBatchValidationError>> = [];
  const warnings: Array<ValidationError<ReceiptBatchValidationError>> = [];
  const missingResidents: number[] = [];
  const missingAmounts: number[] = [];
  const missingDates: number[] = [];
  const missingImages: number[] = [];

  receipts.forEach((receipt, index) => {
    // Check for resident
    if (!receipt.residentId) {
      missingResidents.push(index + 1);
    }

    // Check for amount
    if (!receipt.amount || receipt.amount <= 0) {
      missingAmounts.push(index + 1);
    }

    // Check for individual dates if mode is enabled
    if (useIndividualDates && !receipt.individualDate) {
      missingDates.push(index + 1);
    }

    // Check for individual receipt images if required and auto-generation is off
    if (!autoGenerateReceipts) {
      const hasReceiptImage =
        receipt.receiptImageIds &&
        receipt.receiptImageIds.length > 0 &&
        receipt.receiptImageIds.some((id) => id && id.trim() !== '');

      if (!hasReceiptImage) {
        missingImages.push(index + 1);
      }
    }
  });

  // Add aggregated errors with details about which receipts have issues
  if (missingResidents.length > 0) {
    errors.push({
      type: ReceiptBatchValidationError.MISSING_RECEIPT_RESIDENT,
      translationKey: 'receipts.batch.validation.missing_residents',
      details: {
        receiptNumbers: missingResidents.map((n) => `#${n}`).join(', '),
      },
    });
  }

  if (missingAmounts.length > 0) {
    errors.push({
      type: ReceiptBatchValidationError.MISSING_RECEIPT_AMOUNT,
      translationKey: 'receipts.batch.validation.missing_amounts',
      details: {
        receiptNumbers: missingAmounts.map((n) => `#${n}`).join(', '),
      },
    });
  }

  if (missingDates.length > 0) {
    errors.push({
      type: ReceiptBatchValidationError.MISSING_RECEIPT_DATE,
      translationKey: 'receipts.batch.validation.missing_individual_dates',
      details: { receiptNumbers: missingDates.map((n) => `#${n}`).join(', ') },
    });
  }

  if (missingImages.length > 0) {
    if (receiptImagesRequired) {
      errors.push({
        type: ReceiptBatchValidationError.MISSING_RECEIPT_IMAGE,
        translationKey: 'receipts.batch.validation.missing_receipt_images',
        details: {
          receiptNumbers: missingImages.map((n) => `#${n}`).join(', '),
        },
      });
    } else if (!showMissingBatchImageWarning) {
      // if the batch image warning is already shown, we don't need to show individual receipt warnings as well
      warnings.push({
        type: ReceiptBatchValidationError.MISSING_RECEIPT_IMAGE,
        translationKey: 'mobile.receipt.submit-receipt-images-warning',
        details: {
          receiptNumbers: missingImages.map((n) => `#${n}`).join(', '),
        },
      });
    }
  }

  return { errors, warnings };
}

export function canSubmitReceiptBatch(
  context: ReceiptBatchValidationContext
): boolean {
  const { errors } = getReceiptBatchSubmissionErrors(context);
  return errors.length === 0;
}

export function getValidationSummary(
  errors: Array<ValidationError<ReceiptBatchValidationError>>
): {
  hasErrors: boolean;
  errorCount: number;
  errorTypes: ReceiptBatchValidationError[];
  hasCriticalErrors: boolean;
} {
  const criticalErrors = [
    ReceiptBatchValidationError.UNSAVED_CHANGES,
    ReceiptBatchValidationError.NO_RECEIPTS_IN_BATCH,
  ];

  return {
    hasErrors: errors.length > 0,
    errorCount: errors.length,
    errorTypes: errors.map((e) => e.type),
    hasCriticalErrors: errors.some((e) => criticalErrors.includes(e.type)),
  };
}
